import { Component, inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DiagnocatTrackerService } from '../../../core/services/diagnocat-tracker.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-diagnocat-floating-status',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div *ngIf="status$ | async as status" 
         [class.hidden]="!status.isProcessing && !status.reportData && !status.error"
         class="fixed z-[60] transition-all duration-300 ease-out flex items-end justify-end pointer-events-none"
         [style.right.px]="position.right"
         [style.bottom.px]="position.bottom">
      
      <!-- MAIN CONTAINER: One element that expands -->
      <div (mousedown)="startDragging($event)"
           class="pointer-events-auto cursor-move flex items-center gap-3 overflow-hidden transition-all duration-500 ease-in-out border shadow-2xl relative"
           [ngClass]="{
             'w-14 h-14 rounded-full bg-white border-slate-100 animate-bubble-in': !isExpanded,
             'w-72 h-auto min-h-[70px] rounded-2xl p-3 bg-white/95 backdrop-blur-md border-slate-200 animate-expand': isExpanded,
             'shadow-emerald-500/10': isExpanded && status.reportData,
             'shadow-rose-500/10': isExpanded && status.error
           }">
        
        <!-- Animated Ring (Only in bubble mode) -->
        <div *ngIf="!isExpanded" class="absolute inset-0 rounded-full border-2 border-slate-100/50"></div>
        <div *ngIf="!isExpanded" class="absolute inset-0 rounded-full border-t-2 border-primary animate-spin-fast shadow-glow"></div>

        <!-- 1. ICON SECTION (Stable Position) -->
        <div class="shrink-0 transition-all duration-500 ease-in-out flex items-center justify-center relative"
             [ngClass]="{
               'w-full h-full': !isExpanded,
               'w-11 h-11 rounded-xl': isExpanded,
               'bg-emerald-50 border border-emerald-100': isExpanded && status.reportData,
               'bg-rose-50 border border-rose-100': isExpanded && status.error
             }">
          <img src="assets/images/dignocat.png" 
               class="object-contain mix-blend-multiply transition-all duration-500"
               [ngClass]="isExpanded ? 'w-7 h-7' : 'w-8 h-8'"
               alt="AI">
        </div>

        <!-- 2. CONTENT SECTION (Revealed on expansion) -->
        <div *ngIf="isExpanded" class="flex-1 min-w-0 flex flex-col gap-0.5 animate-fade-in pr-6">
           <div class="flex items-center justify-between pointer-events-none">
              <span class="text-[9px] font-black uppercase tracking-widest"
                    [ngClass]="status.reportData ? 'text-emerald-600' : 'text-rose-600'">
                {{ 'DIAGNOCAT' | translate }}
              </span>
           </div>
           
           <h4 class="text-[12px] font-bold text-slate-800 truncate leading-tight">
             {{ status.patientName || ('ANALYSIS_COMPLETE' | translate) }}
           </h4>
           
           <p class="text-[10px] font-semibold truncate" 
              [ngClass]="status.reportData ? 'text-emerald-500' : 'text-rose-500'">
             {{ getStatusText(status) | translate }}
           </p>

           <!-- Actions -->
           <div class="flex gap-2 mt-2" (mousedown)="$event.stopPropagation()">
              <button *ngIf="status.reportData" 
                      (click)="onViewReport()"
                      class="flex-1 bg-primary text-white text-[9px] font-black uppercase tracking-widest py-1.5 rounded-lg hover:bg-primary/90 transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1.5 shadow-sm">
                <i class="pi pi-eye text-[10px]"></i>
                {{ 'VIEW_REPORT' | translate }}
              </button>
              
              <button *ngIf="status.error" 
                      (click)="onDismiss($event)"
                      class="flex-1 bg-rose-50 text-rose-500 border border-rose-100 text-[9px] font-black uppercase tracking-widest py-1.5 rounded-lg hover:bg-rose-100 transition-all cursor-pointer">
                {{ 'DISMISS' | translate }}
              </button>
           </div>
        </div>

        <!-- Close Button (Top right in expanded mode) -->
        <button *ngIf="isExpanded" 
                (mousedown)="$event.stopPropagation()" 
                (click)="onDismiss($event)"
                class="absolute top-2.5 right-2.5 text-slate-300 hover:text-slate-500 transition-colors cursor-pointer p-1">
          <i class="pi pi-times text-[10px]"></i>
        </button>
      </div>

    </div>
  `,
  styles: [`
    .animate-spin-fast {
      animation: spin 0.8s linear infinite;
    }

    .shadow-glow {
      box-shadow: 0 0 10px rgba(58, 102, 175, 0.3);
    }

    .animate-bubble-in {
      animation: bubbleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }

    .animate-expand {
      animation: expandIn 0.4s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    }

    .animate-fade-in {
      animation: fadeIn 0.4s ease-out 0.2s forwards;
      opacity: 0;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    @keyframes bubbleIn {
      from { transform: scale(0) rotate(-20deg); opacity: 0; }
      to { transform: scale(1) rotate(0); opacity: 1; }
    }

    @keyframes expandIn {
      from { 
        width: 56px;
        height: 56px;
        border-radius: 50%;
      }
      to { 
        width: 288px;
        border-radius: 1rem;
      }
    }

    @keyframes fadeIn {
      from { opacity: 0; transform: translateX(10px); }
      to { opacity: 1; transform: translateX(0); }
    }
  `]
})
export class DiagnocatFloatingStatusComponent {
  private service = inject(DiagnocatTrackerService);
  status$ = this.service.status$;

  // Draggable State (Anchored to Bottom-Right)
  position = { right: 20, bottom: 30 };
  private dragging = false;
  private offset = { right: 0, bottom: 0 };
  isExpanded = false;

  constructor() {
    this.status$.subscribe(status => {
      this.isExpanded = !!(status.reportData || status.error);
    });
  }

  startDragging(event: MouseEvent) {
    this.dragging = true;
    this.offset = {
      right: window.innerWidth - event.clientX - this.position.right,
      bottom: window.innerHeight - event.clientY - this.position.bottom
    };
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (this.dragging) {
      const margin = 10;
      const width = this.isExpanded ? 288 : 56;
      const height = this.isExpanded ? 100 : 56;

      let r = window.innerWidth - event.clientX - this.offset.right;
      let b = window.innerHeight - event.clientY - this.offset.bottom;

      // Ensure it stays on screen
      r = Math.max(margin, Math.min(r, window.innerWidth - width - margin));
      b = Math.max(margin, Math.min(b, window.innerHeight - height - margin));

      this.position = { right: r, bottom: b };
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.dragging = false;
  }

  getStatusText(status: any): string {
    if (status.isProcessing) return 'ANALYSIS_IN_PROGRESS';
    if (status.reportData) return 'SUCCESS_ANALYSIS_READY';
    if (status.error) {
       if (typeof status.error === 'object' && status.error.error) return status.error.error;
       return this.service.errorText(status) ?? '';
    }
    return '';
  }

  onViewReport() {
    this.service.viewReport();
  }

  onDismiss(event: Event) {
    event.stopPropagation();
    this.service.dismiss();
  }
}
